<script setup lang="ts">
import { cn } from '@/lib/utils'
import httpClient from '@/utils/httpClient'
import { CopyIcon, EyeClosedIcon, EyeOpenIcon, UpdateIcon } from '@radix-icons/vue'
import { UseClipboard } from '@vueuse/components'
import { LoaderIcon } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import { Button } from '../button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../tooltip'

const model = defineModel<string>()

const props = withDefaults(
    defineProps<{
        placeholder?: string
        disabled?: boolean
        canGenerate?: boolean
        label?: string | null
        generate?: boolean
    }>(),
    {
        disabled: false,
        placeholder: 'Password',
        canGenerate: true,
        label: null,
        generate: false,
    }
)

const emits = defineEmits<{
    (e: 'update:modelValue', payload: string | undefined): void
}>()

const type = ref('password')
const generatingNewPassword = ref(false)

const value = computed({
    get() {
        return model.value
    },
    set(value) {
        emits('update:modelValue', value)
    },
})

onMounted(() => {
    if (props.generate) {
        generateNewPassword()
    }
})

function generateNewPassword() {
    generatingNewPassword.value = true
    httpClient
        .get('password')
        .then(({ data }) => {
            generatingNewPassword.value = false
            value.value = data.password
        })
        .catch(() => {
            generatingNewPassword.value = false
        })
}

function toggleType() {
    if (type.value === 'password') {
        type.value = 'text'
    } else {
        type.value = 'password'
    }
}
</script>

<template>
    <div
        :class="
            cn(
                'flex h-9 w-full rounded-md border border-input bg-transparent space-x-1 px-3 py-1 text-sm shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground has-focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50',
                $attrs.class ?? ''
            )
        "
    >
        <div
            v-if="label"
            class="flex items-center mr-2 font-semibold shrink-0 text-muted-foreground"
        >
            {{ label }}
        </div>
        <input
            v-model="value"
            :type="type"
            :placeholder="placeholder"
            :disabled="disabled || generatingNewPassword"
            class="flex-1"
        />
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger as-child>
                    <Button variant="icon" size="icon-sm" @click="toggleType">
                        <EyeClosedIcon v-if="type === 'password'" class="h-3 w-3" />
                        <EyeOpenIcon v-else class="h-3 w-3" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    <p v-if="type === 'password'">Reveal</p>
                    <p v-else>Conceal</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
        <TooltipProvider v-if="canGenerate">
            <Tooltip>
                <TooltipTrigger as-child>
                    <Button variant="icon" size="icon-sm" @click="generateNewPassword">
                        <UpdateIcon v-if="generatingNewPassword === false" class="h-3 w-3" />
                        <LoaderIcon v-else class="h-3 w-3 animate-spin" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Generate new password</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>

        <TooltipProvider disable-closing-trigger>
            <UseClipboard v-slot="{ copy, copied }" :source="value">
                <Tooltip>
                    <TooltipTrigger as-child>
                        <Button variant="icon" size="icon-sm" @click="copy(value)">
                            <CopyIcon class="h-3 w-3" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent as-child>
                        <p v-if="!copied">Copy</p>
                        <p v-else>Copied!</p>
                    </TooltipContent>
                </Tooltip>
            </UseClipboard>
        </TooltipProvider>
    </div>
</template>
